import React, { memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
  ICalendarWorkTemplateBase,
  IDateRangeISO,
  PopperPlacement,
} from "@miplatform/types";
import { getDate, getDayTitle } from "@miplatform/utils";
import { Box, Grid } from "@mui/material";
import {
  MiWorkBoard,
  MiWorkBoardColumn,
  MiWorkBoardItem,
} from "../../../../components/MiWorkBoard";
import { useCalendarContext } from "../../../../providers";
import { OrderInfoCard as OrderInfoCardUntyped } from "../../../Orders/OrdersList/OrderInfoCard/OrderInfoCard";
import { NhcOrderCard } from "../../NhcOrderCard";
import { OrderAccordion } from "./OrderAccordion";

const MIN_COLUMN_WIDTH = 300;

type DayOrders = {
  byTemplate: Record<string, IAttemptListResponse[]>;
  templateNames: string[];
  count: number;
};

export const KanbanCalendarMainBoard = memo(
  ({
    selectedTemplates,
    orderReasons,
    setSelectedOrder,
  }: KanbanCalendarMainBoardProps) => {
    const {
      events,
      date,
      dateRange,
      calendarSet,
      calendarConfig,
      isCalendarLoading,
      refreshData = () => null,
    } = useCalendarContext();

    const orders = useMemo(() => {
      const workBaseTemplateIds = new Set(
        calendarSet?.config.workTemplateBases?.map(({ id }) => id) || []
      );
      const isAttemptRelevant = ({ assignment }: IAttemptListResponse) =>
        workBaseTemplateIds.has(
          assignment?.order?.orderTemplateId ||
            assignment?.workOrder?.workOrderTemplateId ||
            ""
        );

      return (Object.values(events) as IAttemptListResponse[]).filter(
        (attempt) => isAttemptRelevant(attempt)
      );
    }, [events, calendarSet?.config.id]);

    const workBaseRecords: Record<string, ICalendarWorkTemplateBase> =
      calendarSet?.config.workTemplateBases.reduce((acc, val) => {
        acc[val.name] = val;
        return acc;
      }, {} as Record<string, ICalendarWorkTemplateBase>) ?? {};

    const groupedOrders = useMemo(() => {
      const orderDays: Record<string, DayOrders> = {};

      const filteredOrders =
        selectedTemplates.length === 0
          ? orders
          : orders.filter(
              (order) =>
                order.assignment?.order?.orderTemplateId &&
                selectedTemplates.includes(
                  order.assignment?.order?.orderTemplateId
                )
            );

      filteredOrders.forEach((order: IAttemptListResponse) => {
        const templateName = order.assignment?.order?.orderTemplateName;
        if (!templateName) return;

        const orderDay = getDayTitle(order.displayDate);

        if (!orderDays[orderDay]) {
          orderDays[orderDay] = {
            byTemplate: {},
            templateNames: [],
            count: 0,
          };
        }

        const dayOrders = orderDays[orderDay];
        if (!dayOrders.byTemplate[templateName]) {
          dayOrders.byTemplate[templateName] = [];
          dayOrders.templateNames.push(templateName);
        }
        dayOrders.byTemplate[templateName].push(order);
        dayOrders.count += 1;
      });

      return orderDays;
    }, [orders, selectedTemplates, events.length]);

    const getDateRangeTitles = (range: IDateRangeISO) => {
      const dayTitles: string[] = [];
      if (!range.startDate) return dayTitles;
      const start = getDate(range.startDate);
      const end = range.endDate ? getDate(range.endDate) : start;
      let current = start.clone();
      while (current.isSameOrBefore(end, "day")) {
        dayTitles.push(getDayTitle(current));
        current = current.add(1, "days");
      }
      return dayTitles;
    };

    const columnHeaders = useMemo(() => {
      return getDateRangeTitles(dateRange);
    }, [dateRange, date, calendarConfig]);

    const getColumnSx = useMemo(() => {
      if (columnHeaders.length >= 3)
        return { xs: 12, sm: 6, md: 6, lg: 6, xl: 6 };
      if (columnHeaders.length === 2)
        return { xs: 12, sm: 12, md: 6, lg: 4, xl: 2 };
      return { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 };
    }, [columnHeaders.length]);

    const { classes } = useKanbanCalendarMainBoardStyles({
      columnCount: columnHeaders.length,
    });

    return (
      <MiWorkBoard>
        <Box className={classes.boardScrollContainer}>
          <Grid container spacing={1} className={classes.columnsContainer}>
            {columnHeaders.map((date) => {
              const dayOrders = groupedOrders[date];

              return (
                <Grid
                  size={12 / columnHeaders.length}
                  key={date}
                  className={classes.dayColumn}
                >
                  <MiWorkBoardColumn
                    title={date}
                    loading={isCalendarLoading}
                    count={dayOrders?.count ?? 0}
                  >
                    {dayOrders?.templateNames.map((templateName) => (
                      <OrderAccordion
                        key={templateName}
                        count={dayOrders.byTemplate[templateName].length}
                        title={templateName}
                        backgroundColor={workBaseRecords[templateName]?.color}
                      >
                        <Grid container spacing={2}>
                          {dayOrders.byTemplate[templateName].map(
                            (order: IAttemptListResponse) => {
                              const holdType = order?.assignment?.order
                                ?.holdCalendarId
                                ? calendarSet?.calendars?.[
                                    order?.assignment?.order?.holdCalendarId
                                  ]?.config?.name
                                : undefined;
                              return (
                                <Grid
                                  size={getColumnSx}
                                  key={order.id}
                                  className={classes.orderCardGrid}
                                >
                                  <MiWorkBoardItem key={order.id}>
                                    <NhcOrderCard
                                      data={order}
                                      onRefresh={refreshData}
                                      orderReasons={orderReasons}
                                      setSelectedOrder={setSelectedOrder}
                                      calendarSet={calendarSet}
                                      headerColor={
                                        workBaseRecords[templateName]?.color
                                      }
                                      holdType={holdType}
                                      popperPlacement={PopperPlacement.Right}
                                      popperComponent={
                                        <OrderInfoCard
                                          order={{
                                            home: order.home,
                                            ...order?.assignment?.order,
                                            notes:
                                              order?.assignment?.order
                                                ?.orderNotes,
                                          }}
                                          holdType={holdType}
                                        />
                                      }
                                    />
                                  </MiWorkBoardItem>
                                </Grid>
                              );
                            }
                          )}
                        </Grid>
                      </OrderAccordion>
                    ))}
                  </MiWorkBoardColumn>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </MiWorkBoard>
    );
  }
);

const useKanbanCalendarMainBoardStyles = makeStyles<{ columnCount: number }>()(
  (theme, { columnCount }) => ({
    boardScrollContainer: {
      overflowX: "auto",
      overflowY: "auto",
      padding: theme.spacing(3),
      height: "100%",
    },
    columnsContainer: {
      minHeight: "100%",
      minWidth:
        columnCount * MIN_COLUMN_WIDTH + Math.max(columnCount - 1, 0) * 4,
      flexWrap: "nowrap",
    },
    dayColumn: {
      minHeight: "100%",
      minWidth: MIN_COLUMN_WIDTH,
      flexShrink: 0,
    },
    orderCardGrid: {
      minWidth: 150,
    },
  })
);

const OrderInfoCard = OrderInfoCardUntyped as React.FC<{
  order: any;
  fieldsConfig?: any;
  holdType?: string;
}>;

interface KanbanCalendarMainBoardProps {
  selectedTemplates: string[];
  setSelectedOrder: (
    order:
      | IAttemptListResponse
      | IAttemptListItemResponseForWorkOrder
      | undefined
  ) => void;
  orderReasons: {
    holdReasons: { value: string; label: string }[];
    rescheduleReasons: { value: string; label: string }[];
  };
}
